// Settings -> Inventory tab: the tenant's supplier catalogue and negotiated pricing.
//
// Upload a supplier price list (PDF/CSV/Excel) -> it is extracted into line items -> search,
// filter and edit them here. Extraction is good but not perfect, so every field is editable and
// every row keeps the import and page it came from.
//
// Lives in its own file rather than inside TenantSettingsPage.tsx (already 3,000 lines) because
// it owns real state: a debounced search, pagination, per-row inline editing and an upload flow.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSearchInventoryQuery,
  useGetInventoryFacetsQuery,
  useListPriceListImportsQuery,
  useImportPriceListMutation,
  useUpdateInventoryLineMutation,
  useDeleteInventoryLineMutation,
  useCreateInventoryLineMutation,
  type InventoryLine,
  type InventoryLineUpdate,
  type PriceListImport,
} from '@/redux/internal-services/inventoryApi';
import { apiErrorMessage } from '@/utils/apiError';
import { Button, buttonVariants } from '@/components/UI/Button';
import { Badge } from '@/components/UI/Badge';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { SearchInput } from '@/components/UI/SearchInput';
import { Switch } from '@/components/UI/Switch';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

const PAGE_SIZE = 25;

const money = (value?: number | null, currency = 'USD') =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);

type Toast = { type: 'success' | 'error'; msg: string };

// ── Upload panel ──────────────────────────────────────────────────────────────

const UploadPanel: React.FC<{
  onDone: (toast: Toast) => void;
}> = ({ onDone }) => {
  const { t } = useTranslation();
  const [importPriceList, { isLoading }] = useImportPriceListMutation();
  const [supplierName, setSupplierName] = useState('');
  const [lastResult, setLastResult] = useState<PriceListImport | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setLastResult(null);
    try {
      const result = await importPriceList({
        file,
        supplierName: supplierName.trim() || undefined,
      }).unwrap();
      setLastResult(result);
      onDone({
        type: 'success',
        msg: `${result.item_count} items read from ${result.file_name}.`,
      });
    } catch (error) {
      onDone({ type: 'error', msg: apiErrorMessage(error, t, 'That price list could not be read.') });
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="card-surface p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-ui-text">Import a supplier price list</h3>
      <p className="mt-1 text-xs text-ui-text2">
        PDF, CSV or Excel. Products and prices are read from the file and added below — prices
        you already have are updated, not duplicated.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label
            htmlFor="inventory-supplier-name"
            className="eyebrow mb-1 block"
          >
            Supplier name (optional)
          </label>
          <input
            id="inventory-supplier-name"
            type="text"
            value={supplierName}
            onChange={(event) => setSupplierName(event.target.value)}
            placeholder="Read from the file if left blank"
            className={fieldClass({ size: 'sm' })}
          />
        </div>
        {/* A native <label htmlFor> opens the file dialog through the browser's own mechanism —
            more reliable than a programmatic .click(), same approach as the logo uploader. */}
        <label
          htmlFor="inventory-price-list-file"
          className={cn(
            buttonVariants({ variant: 'primary', size: 'md' }),
            'cursor-pointer',
            isLoading && 'pointer-events-none opacity-60'
          )}
        >
          {isLoading ? (
            <>
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Reading file…
            </>
          ) : (
            'Choose file'
          )}
        </label>
        <input
          ref={inputRef}
          id="inventory-price-list-file"
          type="file"
          accept=".pdf,.csv,.tsv,.xlsx,.xlsm"
          className="hidden"
          disabled={isLoading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
      </div>

      {isLoading && (
        <p className="mt-3 text-xs text-ui-text2">
          A PDF is read page by page and can take a minute or two on a long list — you can leave
          this open.
        </p>
      )}

      {lastResult && (
        <div className="mt-4 rounded-control border border-ui-border bg-ui-subtle p-3 text-xs">
          <p className="font-medium text-ui-text">
            {lastResult.supplier_name || 'Unknown supplier'}
            {lastResult.branch ? ` · ${lastResult.branch}` : ''}
          </p>
          <p className="mt-1 text-ui-text2">
            {lastResult.item_count} items · {lastResult.products_created} new,{' '}
            {lastResult.products_updated} already on file · {lastResult.prices_created} new prices,{' '}
            {lastResult.prices_updated} repriced
            {lastResult.effective_from ? ` · effective ${lastResult.effective_from}` : ''}
          </p>
          {lastResult.skipped.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-ui-text2">
                {lastResult.skipped.length} row(s) could not be read
              </summary>
              <ul className="mt-1 list-disc pl-4 text-ui-text2">
                {lastResult.skipped.slice(0, 20).map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// ── Editable row ──────────────────────────────────────────────────────────────

const FIELD_CLASS = fieldClass({ size: 'sm' });

const EditRow: React.FC<{
  line: InventoryLine;
  onClose: () => void;
  onToast: (toast: Toast) => void;
}> = ({ line, onClose, onToast }) => {
  const { t } = useTranslation();
  const [updateLine, { isLoading }] = useUpdateInventoryLineMutation();
  const [deleteLine, { isLoading: deleting }] = useDeleteInventoryLineMutation();
  const [draft, setDraft] = useState<InventoryLineUpdate>({});

  const value = <K extends keyof InventoryLine & keyof InventoryLineUpdate>(key: K) =>
    (draft[key] ?? line[key] ?? '') as string | number;

  const set = (key: keyof InventoryLineUpdate, next: string | number | boolean | undefined) =>
    setDraft((previous) => ({ ...previous, [key]: next }));

  const save = async () => {
    // Only what actually changed is sent — the service treats an omitted field as "leave alone".
    if (Object.keys(draft).length === 0) {
      onClose();
      return;
    }
    try {
      await updateLine({ code: line.supplier_product_code, changes: draft }).unwrap();
      onToast({ type: 'success', msg: 'Item updated.' });
      onClose();
    } catch (error) {
      onToast({ type: 'error', msg: apiErrorMessage(error, t, 'Could not update that item.') });
    }
  };

  const remove = async () => {
    try {
      await deleteLine(line.supplier_product_code).unwrap();
      onToast({ type: 'success', msg: 'Item removed.' });
      onClose();
    } catch (error) {
      onToast({ type: 'error', msg: apiErrorMessage(error, t, 'Could not remove that item.') });
    }
  };

  const field = (
    label: string,
    key: keyof InventoryLineUpdate,
    options: { type?: string; hint?: string; placeholder?: string } = {}
  ) => (
    <label className="block">
      <span className="eyebrow block">
        {label}
      </span>
      <input
        type={options.type ?? 'text'}
        step={options.type === 'number' ? 'any' : undefined}
        value={value(key as keyof InventoryLine & keyof InventoryLineUpdate)}
        placeholder={options.placeholder}
        onChange={(event) =>
          set(
            key,
            options.type === 'number'
              ? event.target.value === ''
                ? undefined
                : Number(event.target.value)
              : event.target.value
          )
        }
        className={cn(FIELD_CLASS, 'mt-1')}
      />
      {options.hint && <span className="mt-1 block text-xs text-ui-text2">{options.hint}</span>}
    </label>
  );

  return (
    <tr className="bg-ui-subtle">
      <td colSpan={8} className="td-cell">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {field('Description', 'description')}
          {field('Category', 'category')}
          {field('Brand', 'brand')}
          {field('Supplier SKU', 'supplier_sku')}

          {field('Price', 'price', { type: 'number' })}
          {field('Price UOM', 'price_uom', { placeholder: 'BD, SQ, EA…' })}
          {field('Alt. price', 'alt_price', { type: 'number' })}
          {field('Alt. UOM', 'alt_price_uom')}

          {field('Effective from', 'effective_from', {
            type: 'date',
            hint: 'A new date adds a new price and keeps the old one',
          })}
          {field('Effective to', 'effective_to', { type: 'date' })}
          {field('Branch', 'branch')}
          {field('Purchase UOM', 'purchase_uom')}

          {field('Pack quantity', 'supplier_pack_quantity', { type: 'number' })}
          {field('Min. order qty', 'minimum_order_quantity', { type: 'number' })}
          {field('Order multiple', 'order_multiple', { type: 'number' })}
          {field('Lead time (days)', 'lead_time_days', { type: 'number' })}

          {field('Catalogue item code', 'item_code', {
            hint: 'Links this price to the estimate catalogue',
          })}
          {field('Material / item class', 'material_code')}
          {field('Notes', 'notes')}

          <label className="block">
            <span className="eyebrow block">
              Status
            </span>
            <select
              value={(draft.status ?? line.status) as string}
              onChange={(event) => set('status', event.target.value)}
              className={cn(FIELD_CLASS, 'mt-1')}
            >
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-ui-text">
              <Switch
                checked={Boolean(draft.preferred ?? line.preferred)}
                onCheckedChange={(next) => set('preferred', next)}
                aria-label="Preferred supplier for this product"
              />
              Preferred supplier for this product
            </label>
            {line.derived_conversion_factor && (
              <span className="text-xs text-ui-text2">
                This list states {line.derived_conversion_factor} {line.price_uom} per{' '}
                {line.alt_price_uom}
              </span>
            )}
            {line.source_page && (
              <span className="text-xs text-ui-text2">
                From {line.import_code} · page {line.source_page}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="destructive-ghost" size="sm" onClick={remove} disabled={deleting}>
              {deleting ? 'Removing…' : 'Remove'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={isLoading}>
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
};

// ── Add row ───────────────────────────────────────────────────────────────────

const AddLineForm: React.FC<{ onClose: () => void; onToast: (toast: Toast) => void }> = ({
  onClose,
  onToast,
}) => {
  const { t } = useTranslation();
  const [createLine, { isLoading }] = useCreateInventoryLineMutation();
  const [form, setForm] = useState({
    supplier_name: '',
    description: '',
    category: '',
    brand: '',
    supplier_sku: '',
    price: '',
    price_uom: '',
  });

  const submit = async () => {
    if (!form.supplier_name.trim() || !form.description.trim()) {
      onToast({ type: 'error', msg: 'A supplier and a description are required.' });
      return;
    }
    try {
      await createLine({
        supplier_name: form.supplier_name.trim(),
        description: form.description.trim(),
        category: form.category.trim() || undefined,
        brand: form.brand.trim() || undefined,
        supplier_sku: form.supplier_sku.trim() || undefined,
        price: form.price === '' ? undefined : Number(form.price),
        price_uom: form.price_uom.trim() || undefined,
      }).unwrap();
      onToast({ type: 'success', msg: 'Item added.' });
      onClose();
    } catch (error) {
      onToast({ type: 'error', msg: apiErrorMessage(error, t, 'Could not add that item.') });
    }
  };

  const input = (key: keyof typeof form, placeholder: string, type = 'text') => (
    <input
      type={type}
      step={type === 'number' ? 'any' : undefined}
      value={form[key]}
      placeholder={placeholder}
      onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
      className={FIELD_CLASS}
    />
  );

  return (
    <div className="card-surface p-4">
      <h3 className="text-sm font-semibold text-ui-text">Add an item manually</h3>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {input('supplier_name', 'Supplier *')}
        {input('description', 'Description *')}
        {input('category', 'Category')}
        {input('brand', 'Brand')}
        {input('supplier_sku', 'Supplier SKU')}
        {input('price', 'Price', 'number')}
        {input('price_uom', 'Price UOM')}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={isLoading}>
          {isLoading ? 'Adding…' : 'Add item'}
        </Button>
      </div>
    </div>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────

const InventorySection: React.FC = () => {
  const [toast, setToast] = useState<Toast | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [supplierCode, setSupplierCode] = useState('');
  const [category, setCategory] = useState('');
  const [mapped, setMapped] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showImports, setShowImports] = useState(false);

  const showToast = useCallback((next: Toast) => {
    setToast(next);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useMemo(
    () => ({
      search: search || undefined,
      supplier_code: supplierCode || undefined,
      category: category || undefined,
      mapped: mapped === '' ? undefined : mapped === 'true',
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    [search, supplierCode, category, mapped, page]
  );

  const { data, isLoading, isFetching } = useSearchInventoryQuery(query);
  const { data: facets } = useGetInventoryFacetsQuery();
  const { data: imports } = useListPriceListImportsQuery(undefined, { skip: !showImports });

  const lines = data?.lines ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const hasFilters = Boolean(search || supplierCode || category || mapped);

  const selectClass = cn(fieldClass({ size: 'sm' }), 'w-auto');

  return (
    <div className="space-y-5">
      {toast && (
        <div
          className={cn(
            'fixed top-4 right-4 z-toast rounded-control px-4 py-3 text-sm text-white shadow-pop',
            toast.type === 'success' ? 'bg-success-fill' : 'bg-danger-fill'
          )}
        >
          {toast.msg}
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold text-ui-text">Inventory</h2>
        <p className="mt-1 text-sm text-ui-text2">
          The products you buy and the prices you negotiated. Link an item to your estimate
          catalogue and estimates will price it from here instead of the platform benchmark.
        </p>
      </div>

      <UploadPanel onDone={showToast} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search description, SKU, category, brand…"
          wrapperClassName="min-w-[240px] max-w-none flex-1"
        />
        <select
          value={supplierCode}
          onChange={(event) => {
            setSupplierCode(event.target.value);
            setPage(0);
          }}
          className={selectClass}
          aria-label="Filter by supplier"
        >
          <option value="">All suppliers</option>
          {(facets?.suppliers ?? []).map((supplier) => (
            <option key={supplier.supplier_code} value={supplier.supplier_code}>
              {supplier.supplier_name}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setPage(0);
          }}
          className={selectClass}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {(facets?.categories ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={mapped}
          onChange={(event) => {
            setMapped(event.target.value as '' | 'true' | 'false');
            setPage(0);
          }}
          className={selectClass}
          aria-label="Filter by catalogue link"
        >
          <option value="">Linked or not</option>
          <option value="true">Linked to catalogue</option>
          <option value="false">Not linked yet</option>
        </select>
        <Button type="button" variant="outline" size="sm" onClick={() => setAdding((previous) => !previous)}>
          {adding ? 'Close' : 'Add item'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setShowImports((previous) => !previous)}>
          {showImports ? 'Hide imports' : 'Import history'}
        </Button>
      </div>

      {adding && <AddLineForm onClose={() => setAdding(false)} onToast={showToast} />}

      {showImports && (
        <div className="table-card overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr>
                <th className="th-cell">File</th>
                <th className="th-cell">Supplier</th>
                <th className="th-cell">Effective</th>
                <th className="th-cell">Items</th>
                <th className="th-cell">Status</th>
              </tr>
            </thead>
            <tbody>
              {(imports ?? []).map((record) => (
                <tr key={record.import_code}>
                  <td className="td-cell">
                    {record.file_url ? (
                      <a
                        href={record.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-dotted"
                      >
                        {record.file_name}
                      </a>
                    ) : (
                      record.file_name
                    )}
                  </td>
                  <td className="td-cell text-ui-text2">{record.supplier_name || '—'}</td>
                  <td className="td-cell text-ui-text2">{record.effective_from || '—'}</td>
                  <td className="td-cell text-ui-text2">{record.item_count}</td>
                  <td className="td-cell text-ui-text2">
                    {record.status === 'FAILED' ? (
                      <span className="text-danger-text">{record.error || 'Failed'}</span>
                    ) : (
                      record.status
                    )}
                  </td>
                </tr>
              ))}
              {(imports ?? []).length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <EmptyState title="No price list has been imported yet." className="py-6" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Grid */}
      <div className="table-card overflow-x-auto">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr>
              <th className="th-cell">Item</th>
              <th className="th-cell">Category</th>
              <th className="th-cell">Supplier</th>
              <th className="th-cell">SKU</th>
              <th className="th-cell text-right">Price</th>
              <th className="th-cell">Effective</th>
              <th className="th-cell">Catalogue</th>
              <th className="th-cell" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8}>
                  <LoadingState label="Loading inventory…" className="py-10" />
                </td>
              </tr>
            )}

            {!isLoading && lines.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    className="py-10"
                    title={
                      hasFilters
                        ? 'No item matches those filters.'
                        : 'No inventory yet — import a supplier price list above to get started.'
                    }
                  />
                </td>
              </tr>
            )}

            {lines.map((line) =>
              editing === line.supplier_product_code ? (
                <EditRow
                  key={line.supplier_product_code}
                  line={line}
                  onClose={() => setEditing(null)}
                  onToast={showToast}
                />
              ) : (
                <tr key={line.supplier_product_code} className="align-top">
                  <td className="td-cell">
                    <span className="text-ui-text">{line.description}</span>
                    {line.brand && (
                      <span className="ml-2 text-xs text-ui-text2">{line.brand}</span>
                    )}
                    {line.preferred && (
                      <Badge className="ml-2 uppercase">Preferred</Badge>
                    )}
                    {line.status === 'ARCHIVED' && (
                      <span className="ml-2 text-micro uppercase text-ui-text2">Archived</span>
                    )}
                  </td>
                  <td className="td-cell text-ui-text2">{line.category || '—'}</td>
                  <td className="td-cell text-ui-text2">
                    {line.supplier_name || line.supplier_code}
                    {line.branch && <span className="block text-xs">{line.branch}</span>}
                  </td>
                  <td className="td-cell text-ui-text2">{line.supplier_sku || '—'}</td>
                  <td className="td-cell text-right">
                    {line.price === null || line.price === undefined ? (
                      <span className="text-ui-text2">{line.price_note || '—'}</span>
                    ) : (
                      <>
                        {money(line.price, line.currency)}
                        {line.price_uom && (
                          <span className="text-xs text-ui-text2"> /{line.price_uom}</span>
                        )}
                        {line.alt_price !== null && line.alt_price !== undefined && (
                          <span className="block text-xs text-ui-text2">
                            {money(line.alt_price, line.currency)}
                            {line.alt_price_uom ? ` /${line.alt_price_uom}` : ''}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {line.effective_from || '—'}
                    {line.price_row_count > 1 && (
                      <span className="block text-xs">{line.price_row_count} prices on file</span>
                    )}
                  </td>
                  <td className="td-cell text-ui-text2">
                    {line.item_code || line.material_code || (
                      <span className="text-xs">Not linked</span>
                    )}
                  </td>
                  <td className="td-cell text-right">
                    <Button type="button" variant="soft" size="xs" onClick={() => setEditing(line.supplier_product_code)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-ui-text2">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            {isFetching && ' · updating…'}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.max(0, previous - 1))}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.min(lastPage, previous + 1))}
              disabled={page >= lastPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySection;
